import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import styles from './AppSidebar.module.css';
import logo from '../../assets/logo.png';
import responsesApi from '../../api/responsesApi';
import reportsApi from '../../api/reportsApi';

function BarChartIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6"  y1="20" x2="6"  y2="14" />
    </svg>
  );
}

function GridIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  );
}

function ChevronIcon({ open }) {
  return (
    <svg
      width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

export default function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [openDownloads, setOpenDownloads] = useState(false);
  const [openAnalytics, setOpenAnalytics] = useState(false);
  const [openConfig, setOpenConfig] = useState(false);

  const isAnalytics = location.pathname === '/';
  const isEmpresas  = location.pathname === '/empresas';

  // Rol del usuario
  let role = null;
  try {
    const token = localStorage.getItem('token');
    if (token) role = jwtDecode(token).role;
  } catch (_) {}

  // ── Descarga handlers ──────────────────────────────────────────
  const download = async (apiFn, filename, label) => {
    try {
      const blob = await apiFn();
      const url  = window.URL.createObjectURL(new Blob([blob]));
      const a    = document.createElement('a');
      a.href     = url;
      a.setAttribute('download', filename);
      document.body.appendChild(a);
      a.click();
      a.parentNode.removeChild(a);
    } catch (err) {
      console.error(`Error descargando ${label}:`, err);
    }
  };

  return (
    <aside className={styles.sidebar}>
      {/* Logo */}
      <div className={styles.logoWrapper}>
        <img src={logo} alt="Deli Life" className={styles.logoImg} />
        <div className={styles.logoText}>
          <span className={styles.logoName}>DELI LIFE</span>
          <span className={styles.logoSub}>WELLNESS</span>
        </div>
      </div>

      {/* Nav principal */}
      <nav className={styles.nav}>
        <button
          className={`${styles.navItem} ${isAnalytics ? styles.navItemActive : ''}`}
          onClick={() => navigate('/')}
        >
          <BarChartIcon />
          <span>Analítica Global</span>
        </button>

        <button
          className={`${styles.navItem} ${isEmpresas ? styles.navItemActive : ''}`}
          onClick={() => navigate('/empresas')}
        >
          <GridIcon />
          <span>Empresas</span>
        </button>
      </nav>

      <div className={styles.divider} />

      {/* Secciones colapsables */}
      <nav className={styles.sections}>
        {/* Descargas */}
        <div>
          <button
            className={styles.sectionTitle}
            onClick={() => setOpenDownloads(v => !v)}
          >
            <span>Descargas</span>
            <ChevronIcon open={openDownloads} />
          </button>
          {openDownloads && (
            <ul className={styles.nestedList}>
              <li><button className={styles.nestedItem} onClick={() => download(responsesApi.exportHRAResponses,   'hra_responses_all.xlsx',    'HRA global')}>HRA global</button></li>
              <li><button className={styles.nestedItem} onClick={() => download(responsesApi.exportEstresResponses,'estres_responses_all.xlsx', 'Burnout global')}>Burnout global</button></li>
              <li><button className={styles.nestedItem} onClick={() => download(responsesApi.exportHBResponses,    'hb_responses_all.xlsx',     'HRA & Burnout global')}>HRA & Burnout global</button></li>
              <li><button className={styles.nestedItem} onClick={() => download(responsesApi.exportGhqResponses,   'ghq_responses_all.xlsx',    'GHQ global')}>Evaluación Integral global</button></li>
            </ul>
          )}
        </div>

        {/* Analíticas */}
        <div>
          <button
            className={styles.sectionTitle}
            onClick={() => setOpenAnalytics(v => !v)}
          >
            <span>Analíticas</span>
            <ChevronIcon open={openAnalytics} />
          </button>
          {openAnalytics && (
            <ul className={styles.nestedList}>
              <li><button className={styles.nestedItem} onClick={() => navigate('/analytics/hra/global')}>Gráficas Globales HRA</button></li>
              <li><button className={styles.nestedItem} onClick={() => navigate('/analytics/estresBurnout/global')}>Gráficas Globales Burnout</button></li>
              <li><button className={styles.nestedItem} onClick={() => navigate('/analytics/hb/global')}>Gráficas Globales HRA & Burnout</button></li>
              <li><button className={styles.nestedItem} onClick={() => navigate('/analytics/nom035/global')}>Gráficas Globales Nom035</button></li>
              <li><button className={styles.nestedItem} onClick={() => navigate('/analytics/nom035SecI/global')}>Gráficas Globales Nom035 Guía I</button></li>
              <li><button className={styles.nestedItem} onClick={() => navigate('/analytics/ghq/global')}>Gráficas Globales Evaluación Integral</button></li>
            </ul>
          )}
        </div>

        {/* Configuración */}
        <div>
          <button
            className={styles.sectionTitle}
            onClick={() => setOpenConfig(v => !v)}
          >
            <span>Configuración</span>
            <ChevronIcon open={openConfig} />
          </button>
          {openConfig && (
            <ul className={styles.nestedList}>
              <li><button className={styles.nestedItem} onClick={() => navigate('/hraConfiguracion')}>HRA</button></li>
              {role === 'root user' && (
                <li>
                  <button
                    className={styles.nestedItem}
                    onClick={() => download(
                      reportsApi.exportGeneralReport,
                      'informe_general.xlsx',
                      'Informe general'
                    )}
                  >
                    Informe general
                  </button>
                </li>
              )}
              {(role === 'root user' || role === 'super admin') && (
                <li><button className={styles.nestedItem} onClick={() => navigate('/adminConfig')}>Admins</button></li>
              )}
              {(role === 'root user' || role === 'super admin') && (
                <li><button className={styles.nestedItem} onClick={() => navigate('/nutriologyConfig')}>Nutriólogas</button></li>
              )}
              {role === 'root user' && (
                <li><button className={styles.nestedItem} onClick={() => navigate('/superAdminConfig')}>Super admins</button></li>
              )}
              {(role === 'root user' || role === 'super admin') && (
                <li><button className={styles.nestedItem} onClick={() => navigate('/2fa/configuracion')}>Autenticación 2FA</button></li>
              )}
            </ul>
          )}
        </div>
      </nav>

      <div className={styles.divider} />

      <button
        className={styles.logoutItem}
        onClick={() => { localStorage.clear(); navigate('/login'); }}
      >
        Cerrar sesión
      </button>
    </aside>
  );
}
